import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckCircle2, 
  Briefcase, FolderKanban, Target, Shield, Clock, GitBranch, FileCheck,
  ChevronDown, ChevronRight, Users, Calendar, ArrowRight, RefreshCw, Map
} from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useTenant } from '../context/TenantContext';
import HierarchicalRoadmap from '../components/HierarchicalRoadmap';
import DetailDrawerV2 from '../components/DetailDrawerV2';

// ===========================
// SUB-COMPONENTS
// ===========================

// KPI Card with semantic border
const KPICard = ({ title, value, subtitle, icon: Icon, borderColor, onClick, testId }) => {
  return (
    <div 
      className="bg-[var(--clr-surface-card)] rounded-lg border-l-4 p-4 shadow-[var(--shadow-card)] cursor-pointer hover:shadow-[var(--shadow-md)] transition-shadow"
      style={{ borderLeftColor: borderColor }}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        {Icon && <Icon className="w-5 h-5" style={{ color: borderColor }} />}
      </div>
      <p className="text-3xl font-bold text-[var(--clr-text-primary)]">{value}</p>
      <p className="text-sm text-[var(--clr-text-secondary)]">{title}</p>
      {subtitle && <p className="text-xs text-[var(--clr-text-muted)] mt-1">{subtitle}</p>}
    </div>
  );
};

// Program Card with health indicator
const ProgramCard = ({ program, onClick, formatCurrency }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'blocked': return 'var(--clr-sev-blocked)';
      case 'behind': return 'var(--clr-sev-behind)';
      case 'at risk': case 'due date at risk': return 'var(--clr-sev-atrisk)';
      case 'on track': case 'in progress': return 'var(--clr-sev-healthy)';
      case 'completed': return 'var(--clr-sev-completed)';
      default: return 'var(--clr-sev-notstarted)';
    }
  };
  
  const statusColor = getStatusColor(program.status);
  
  return (
    <div 
      className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4 hover:shadow-[var(--shadow-md)] cursor-pointer transition-all border-l-4"
      style={{ borderLeftColor: statusColor }}
      onClick={() => onClick(program)}
      data-testid={`program-card-${program.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-[var(--clr-text-primary)]">{program.name}</h3>
        <span 
          className="px-2 py-0.5 text-xs rounded-full text-white"
          style={{ backgroundColor: statusColor }}
        >
          {program.status || 'Unknown'}
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-[var(--clr-text-secondary)]">Progress</span>
          <span className="font-medium text-[var(--clr-text-primary)]">{program.percent_complete || 0}%</span>
        </div>
        <div className="w-full bg-[var(--clr-border-light)] rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all"
            style={{ 
              width: `${program.percent_complete || 0}%`,
              backgroundColor: statusColor
            }}
          />
        </div>
      </div>
      
      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="text-center">
          <p className="font-semibold text-[var(--clr-text-primary)]">{program.project_count || 0}</p>
          <p className="text-xs text-[var(--clr-text-muted)]">Projects</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-[var(--clr-sev-atrisk)]">{program.risk_count || 0}</p>
          <p className="text-xs text-[var(--clr-text-muted)]">Risks</p>
        </div>
        <div className="text-center">
          <p className="font-semibold text-[var(--clr-sev-blocked)]">{program.blocked_count || 0}</p>
          <p className="text-xs text-[var(--clr-text-muted)]">Blocked</p>
        </div>
      </div>
      
      {/* Dates */}
      {(program.start_date || program.due_date) && (
        <div className="mt-3 pt-3 border-t border-[var(--clr-border-light)] flex items-center justify-between text-xs text-[var(--clr-text-muted)]">
          <span>{program.start_date ? new Date(program.start_date).toLocaleDateString() : 'No start'}</span>
          <ArrowRight className="w-3 h-3" />
          <span>{program.due_date ? new Date(program.due_date).toLocaleDateString() : 'No end'}</span>
        </div>
      )}
    </div>
  );
};

// Issue/Risk Card
const IssueCard = ({ issue, onClick }) => {
  const getRiskColor = (score) => {
    if (score >= 15) return { bg: 'var(--clr-sev-blocked-bg)', text: 'var(--clr-sev-blocked)' };
    if (score >= 10) return { bg: 'var(--clr-sev-atrisk-bg)', text: 'var(--clr-sev-atrisk)' };
    return { bg: 'var(--clr-surface-subtle)', text: 'var(--clr-text-secondary)' };
  };
  
  const riskColor = getRiskColor(issue.risk_score || 0);
  
  return (
    <div 
      className="bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg p-3 hover:shadow-[var(--shadow-sm)] cursor-pointer transition-all border-l-3"
      style={{ borderLeftWidth: '3px', borderLeftColor: riskColor.text }}
      onClick={() => onClick(issue)}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-sm font-medium text-[var(--clr-text-primary)]">{issue.issue_key}</span>
        <span 
          className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ backgroundColor: riskColor.bg, color: riskColor.text }}
        >
          Risk: {issue.risk_score}
        </span>
      </div>
      <p className="text-sm text-[var(--clr-text-secondary)] mb-2 line-clamp-2">{issue.title}</p>
      <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
        <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{issue.type}</span>
        <span>•</span>
        <span>{issue.priority} Priority</span>
        <span>•</span>
        <span className={`${
          issue.status === 'Mitigating' ? 'text-[var(--clr-sev-atrisk)]' :
          issue.status === 'Closed' ? 'text-[var(--clr-sev-healthy)]' :
          'text-[var(--clr-text-muted)]'
        }`}>{issue.status}</span>
      </div>
    </div>
  );
};

// Decision Card
const DecisionCard = ({ decision, onClick }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { bg: 'var(--clr-sev-healthy-bg)', text: 'var(--clr-sev-healthy)' };
      case 'rejected': return { bg: 'var(--clr-sev-blocked-bg)', text: 'var(--clr-sev-blocked)' };
      default: return { bg: 'var(--clr-sev-atrisk-bg)', text: 'var(--clr-sev-atrisk)' };
    }
  };
  
  const statusConfig = getStatusConfig(decision.status);
  
  return (
    <div 
      className="bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg p-3 hover:shadow-[var(--shadow-sm)] cursor-pointer transition-all border-l-3"
      style={{ borderLeftWidth: '3px', borderLeftColor: statusConfig.text }}
      onClick={() => onClick(decision)}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-sm font-medium text-[var(--clr-text-primary)] flex-1 pr-2">{decision.title}</p>
        <span 
          className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
          style={{ backgroundColor: statusConfig.bg, color: statusConfig.text }}
        >
          {decision.status}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
        <span className="px-1.5 py-0.5 bg-[var(--clr-surface-subtle)] rounded">{decision.request_type}</span>
        {decision.request_date && (
          <>
            <span>•</span>
            <span>Requested: {new Date(decision.request_date).toLocaleDateString()}</span>
          </>
        )}
      </div>
    </div>
  );
};

// ===========================
// MAIN COMPONENT
// ===========================

const PortfolioDashboard = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const { filters, getFilterParams } = useFilters();
  const { getCurrency } = useTenant();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [roadmapData, setRoadmapData] = useState(null);
  const [releases, setReleases] = useState([]);
  const [roadmapScale, setRoadmapScale] = useState('quarter');
  const [showReleaseBands, setShowReleaseBands] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    programs: true,
    roadmap: true,
    risks: true,
    decisions: true
  });
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerType, setDrawerType] = useState('work-item');

  useEffect(() => {
    loadDashboard();
  }, [portfolioId, filters]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const filterParams = getFilterParams();
      
      // Load all data in parallel
      const [portfolioRes, roadmapRes, releasesRes] = await Promise.all([
        api.getPortfolioDashboard(portfolioId, filterParams),
        api.getPortfolioRoadmap(portfolioId, 3),
        api.getReleases()
      ]);
      
      setData(portfolioRes.data);
      setRoadmapData(roadmapRes.data?.roadmap || []);
      setReleases(releasesRes.data?.releases || []);
    } catch (error) {
      console.error('Failed to load portfolio dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    setRefreshing(false);
  };

  const formatCurrency = (value) => {
    const currency = getCurrency();
    if (!value && value !== 0) return `${currency}0`;
    if (Math.abs(value) >= 1000000) return `${currency}${(value / 1000000).toFixed(1)}M`;
    if (Math.abs(value) >= 1000) return `${currency}${(value / 1000).toFixed(1)}K`;
    return `${currency}${value.toFixed(0)}`;
  };

  const openDrawer = (item, type = 'work-item') => {
    setSelectedItem(item);
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Briefcase className="w-12 h-12 text-[var(--clr-text-muted)] mr-4" />
        <p className="text-[var(--clr-text-muted)]">Failed to load dashboard data</p>
      </div>
    );
  }

  const { portfolio, kpis, program_roadmap, top_issues, recent_decisions } = data;

  return (
    <div className="space-y-6 bg-[var(--clr-surface-page)] min-h-screen p-6" data-testid="portfolio-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Briefcase className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            {portfolio.name}
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Portfolio Dashboard</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-card)] border border-[var(--clr-border-light)] rounded-lg hover:bg-[var(--clr-surface-hover)] transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <KPICard
          title="Programs"
          value={kpis.program_count}
          subtitle={`${kpis.project_count} projects`}
          icon={FolderKanban}
          borderColor="var(--clr-brand-primary)"
          testId="programs-kpi"
        />
        <KPICard
          title="Completion"
          value={`${kpis.percent_complete}%`}
          subtitle="Overall progress"
          icon={Target}
          borderColor="var(--clr-sev-inprogress)"
          testId="completion-kpi"
        />
        <KPICard
          title="On Track"
          value={kpis.on_track_count}
          subtitle={`${kpis.on_track_pct ?? 0}% of items`}
          icon={CheckCircle2}
          borderColor="var(--clr-sev-healthy)"
          testId="on-track-kpi"
        />
        <KPICard
          title="At Risk"
          value={kpis.at_risk_count}
          subtitle={`${kpis.at_risk_pct ?? 0}% of items`}
          icon={AlertTriangle}
          borderColor="var(--clr-sev-atrisk)"
          testId="at-risk-kpi"
        />
        <KPICard
          title="Est. Cost"
          value={formatCurrency(kpis.cost_metrics?.est_cost)}
          icon={DollarSign}
          borderColor="var(--clr-text-muted)"
          testId="estimated-cost-kpi"
        />
        <KPICard
          title="Act. Cost"
          value={formatCurrency(kpis.cost_metrics?.act_cost)}
          icon={DollarSign}
          borderColor="var(--clr-brand-primary)"
          testId="actual-cost-kpi"
        />
        <KPICard
          title="Variance"
          value={formatCurrency(kpis.cost_metrics?.variance)}
          subtitle={`${kpis.cost_metrics?.variance_pct > 0 ? '+' : ''}${kpis.cost_metrics?.variance_pct || 0}%`}
          icon={kpis.cost_metrics?.variance > 0 ? TrendingUp : TrendingDown}
          borderColor={kpis.cost_metrics?.variance > 0 ? 'var(--clr-sev-blocked)' : 'var(--clr-sev-healthy)'}
          testId="cost-variance-kpi"
        />
      </div>

      {/* Programs Grid */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
        <button
          onClick={() => toggleSection('programs')}
          className="w-full p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between hover:bg-[var(--clr-surface-hover)]"
        >
          <div className="flex items-center gap-3">
            <FolderKanban className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Programs</h2>
            <span className="text-sm text-[var(--clr-text-muted)]">{kpis.program_count} programs</span>
          </div>
          <ChevronDown className={`w-5 h-5 text-[var(--clr-text-muted)] transition-transform ${expandedSections.programs ? 'rotate-180' : ''}`} />
        </button>
        
        {expandedSections.programs && (
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {program_roadmap && program_roadmap.length > 0 ? (
                program_roadmap.map(program => (
                  <ProgramCard
                    key={program.id}
                    program={program}
                    onClick={(p) => navigate(`/program/${p.id}`)}
                    formatCurrency={formatCurrency}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-[var(--clr-text-muted)]">
                  <FolderKanban className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No programs in this portfolio</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Portfolio Roadmap */}
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
        <button
          onClick={() => toggleSection('roadmap')}
          className="w-full p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between hover:bg-[var(--clr-surface-hover)]"
        >
          <div className="flex items-center gap-3">
            <Map className="w-5 h-5 text-[var(--clr-brand-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Portfolio Roadmap</h2>
          </div>
          <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showReleaseBands}
                onChange={(e) => setShowReleaseBands(e.target.checked)}
                className="rounded text-[var(--clr-brand-primary)]"
              />
              <span className="text-[var(--clr-text-secondary)]">Release Bands</span>
            </label>
            <select
              value={roadmapScale}
              onChange={(e) => setRoadmapScale(e.target.value)}
              className="text-sm border border-[var(--clr-border-light)] rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-[var(--clr-brand-primary)] bg-[var(--clr-surface-card)]"
            >
              <option value="year">Year View</option>
              <option value="quarter">Quarter View</option>
              <option value="month">Month View</option>
            </select>
            <ChevronDown className={`w-5 h-5 text-[var(--clr-text-muted)] transition-transform ${expandedSections.roadmap ? 'rotate-180' : ''}`} />
          </div>
        </button>
        
        {expandedSections.roadmap && (
          <div className="p-4">
            <HierarchicalRoadmap 
              data={roadmapData} 
              scale={roadmapScale}
              showReleaseBands={showReleaseBands}
              releases={releases}
              currency={getCurrency()}
              maxDepth={3}
              onItemClick={(item) => {
                // Open drawer for all entity types
                try {
                  const entityType = (item.type || 'work-item').toLowerCase();
                  openDrawer(item, entityType);
                } catch (err) {
                  console.error('Failed to open drawer:', err);
                  // Fallback navigation
                  if (item.type === 'Program') {
                    navigate(`/program/${item.id}`);
                  } else if (item.type === 'Project') {
                    navigate(`/project/${item.id}`);
                  }
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Risks & Decisions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Risks */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
          <button
            onClick={() => toggleSection('risks')}
            className="w-full p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between hover:bg-[var(--clr-surface-hover)]"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-[var(--clr-sev-blocked)]" />
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Top Risks</h2>
              <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--clr-sev-blocked-bg)] text-[var(--clr-sev-blocked)]">
                {top_issues?.length || 0}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-[var(--clr-text-muted)] transition-transform ${expandedSections.risks ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSections.risks && (
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {top_issues && top_issues.length > 0 ? (
                top_issues.map((issue) => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    onClick={() => openDrawer(issue, 'issue')}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-[var(--clr-text-muted)]">
                  <Shield className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No risks reported</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Decisions */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)]">
          <button
            onClick={() => toggleSection('decisions')}
            className="w-full p-4 border-b border-[var(--clr-border-light)] flex items-center justify-between hover:bg-[var(--clr-surface-hover)]"
          >
            <div className="flex items-center gap-3">
              <FileCheck className="w-5 h-5 text-[var(--clr-sev-atrisk)]" />
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Recent Decisions</h2>
              <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)]">
                {recent_decisions?.length || 0}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-[var(--clr-text-muted)] transition-transform ${expandedSections.decisions ? 'rotate-180' : ''}`} />
          </button>
          
          {expandedSections.decisions && (
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {recent_decisions && recent_decisions.length > 0 ? (
                recent_decisions.map((decision) => (
                  <DecisionCard
                    key={decision.id}
                    decision={decision}
                    onClick={() => openDrawer(decision, 'decision')}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-[var(--clr-text-muted)]">
                  <FileCheck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No recent decisions</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer */}
      {drawerOpen && selectedItem && (
        <DetailDrawerV2
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          type={drawerType}
        />
      )}
    </div>
  );
};

export default PortfolioDashboard;
